/**
 * Centralized logging utility
 *
 * Provides conditional logging that can be disabled in production.
 * All console.log/warn/error statements should use this utility instead.
 *
 * Usage:
 * import { logger } from '@core/utils/logger';
 * logger.info('User logged in', { userId: 123 });
 * logger.warn('API response slow', { duration: 3000 });
 * logger.error('Failed to fetch data', error);
 *
 * Environment Configuration:
 * - Development: All logs enabled
 * - Production: Only errors enabled (can be configured)
 */

const isDevelopment = import.meta.env.DEV;

class Logger {
  /**
   * Log informational messages
   * @param {string} message - Log message
   * @param {...any} args - Additional arguments
   */
  info(message, ...args) {
    if (isDevelopment) {
      console.log(`[INFO] ${message}`, ...args);
    }
  }

  /**
   * Log warning messages
   * @param {string} message - Warning message
   * @param {...any} args - Additional arguments
   */
  warn(message, ...args) {
    if (isDevelopment) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  }

  /**
   * Log error messages (always logged, even in production)
   * @param {string} message - Error message
   * @param {...any} args - Additional arguments (typically Error objects)
   */
  error(message, ...args) {
    console.error(`[ERROR] ${message}`, ...args);
  }

  /**
   * Log debug messages (only in development)
   * @param {string} message - Debug message
   * @param {...any} args - Additional arguments
   */
  debug(message, ...args) {
    if (isDevelopment) {
      console.debug(`[DEBUG] ${message}`, ...args);
    }
  }

  /**
   * Group related logs together
   * @param {string} label - Group label
   * @param {Function} callback - Function containing grouped logs
   */
  group(label, callback) {
    if (isDevelopment) {
      console.group(label);
      try {
        callback();
      } finally {
        console.groupEnd();
      }
    }
  }

  /**
   * Log a table (useful for arrays/objects)
   * @param {any} data - Data to display as table
   */
  table(data) {
    if (isDevelopment) {
      console.table(data);
    }
  }

  /**
   * Start a timer
   * @param {string} label - Timer label
   */
  time(label) {
    if (isDevelopment) {
      console.time(label);
    }
  }

  /**
   * End a timer and log duration
   * @param {string} label - Timer label
   */
  timeEnd(label) {
    if (isDevelopment) {
      console.timeEnd(label);
    }
  }
}

export const logger = new Logger();
